import React, { useEffect, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { FaPlus } from 'react-icons/fa';
import { FaX } from 'react-icons/fa6';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory, useRouteMatch } from 'react-router-dom';
import { Box, HStack, Icon, IconButton, ModalFooter, Text, Tooltip } from '@chakra-ui/react';
import PropTypes from 'prop-types';

import { CypressIds } from '../../../../constants/cypressIds';
import { inAppText } from '../../../../constants/inAppText';
import { createSuccessToast, createToast } from '../../../../redux/modules/app';
import { selectCompanyId } from '../../../../redux/modules/company';
import { hideModal } from '../../../../redux/modules/modal';
import { useCompanyRequirementTemplateFolders } from '../../../../services/company-service';
import { getInfiniteListData } from '../../../../services/util';
import colors from '../../../../styles/theme/foundation/colors';
import RequirementTemplateBlueprintType from '../../../../types/RequirementTemplateBlueprintType';
import FormInput from '../../../form/FormInput';
import InputLabel from '../../../form/InputLabel';
import { InfiniteDropdownMenu } from '../../../library';
import StackedButtonGroup from '../../../Modal/ModalButtonGroups/StackedButtonGroup';
import LoadingSpinner from '../../../Spinner';

const SaveTemplateModal = ({ type, blueprint }) => {
  const dispatch = useDispatch();
  const companyId = useSelector(selectCompanyId);
  const useFormMethods = useForm();
  const { handleSubmit, reset } = useFormMethods;
  const history = useHistory();
  const { url } = useRouteMatch();

  const template = useSelector(blueprint.selectCurrentRequirementTemplate);
  const [templateFolder, setTemplateFolder] = useState();
  const [showCreateFolderInput, setShowCreateFolderInput] = useState(false);

  const {
    data: folderData,
    isLoading: folderDataLoading,
    fetchNextPage,
    hasNextPage
  } = useCompanyRequirementTemplateFolders({ companyId });

  const myFolderData = useMemo(() => {
    if (folderDataLoading) {
      return [];
    }

    return getInfiniteListData(folderData);
  }, [folderData, folderDataLoading]);

  useEffect(() => {
    if (template && myFolderData) {
      const defaultFormValues = { name: template.name, description: template.description };

      if (myFolderData.length > 0) {
        let defaultFolder = type === 'publish' ? myFolderData?.[0] : null;

        if (template?.requirementTemplateFolderId) {
          defaultFolder = myFolderData.find(folder => folder.id === template.requirementTemplateFolderId);
        }

        if (template?.stagedFolder) {
          defaultFolder = myFolderData.find(folder => folder.id === template.stagedFolder.id);
        }

        if (defaultFolder) {
          setTemplateFolder(defaultFolder);
        }

        reset(defaultFormValues);
      }
    }
  }, [template, myFolderData, reset, dispatch, type]);

  const formId = type === 'draft' ? 'saveTemplateAsDraft' : 'publishTemplate';

  const onSubmit = async data => {
    let folder = null;

    if (showCreateFolderInput && data?.newFolderName) {
      dispatch(createToast({ title: 'Creating New Folder...', duration: 60000 }));

      folder = await dispatch(blueprint.createRequirementTemplateFolder({ name: data?.newFolderName }));

      dispatch(createSuccessToast({ title: 'Folder Created Successfully!' }));
    } else if (templateFolder) {
      folder = templateFolder;
    }

    dispatch(createToast({ title: 'Updating Template...', duration: 60000 }));

    const templateData = {
      name: data.name,
      description: data.description,
      requirement_template_folder_id: folder?.id
    };

    const updateRequirementTemplateData = type === 'draft' ? { ...templateData, updated_at: new Date() }
      : { ...templateData, updated_at: new Date(), published_at: new Date() };

    await dispatch(blueprint.updateRequirementTemplate(template.id, updateRequirementTemplateData));

    dispatch(createSuccessToast({ title: 'Template Updated Successfully!' }));
    dispatch(hideModal());
    history.push(`${url}/templates/requirement-templates/${folder?.id ?? 'drafts'}`);
  };

  if (folderDataLoading || !template) {
    return (
      <Box>
        <LoadingSpinner isInline minHeight="200px" />
      </Box>
    );
  }

  return (
    <Box>
      <Text
        color={colors.greyScale['600']}
        mb={8}
        textStyle="bodyMd"
      >
        {inAppText.templatesPage.templateBuilder.saveTemplateModal.saveTemplateMessage}
      </Text>
      <form id={formId} onSubmit={handleSubmit(onSubmit)}>
        <FormInput
          required
          cypressId={CypressIds.reqTemplatesSaveTemplateModalTemplateNameInput}
          inputName="name"
          labelName="Template Name"
          placeholder="Template Name"
          useFormMethods={useFormMethods}
          validationRules={{
            required: { value: true, message: 'Template Name is required' },
            maxLength: { value: 191, message: 'Template Name must have fewer than 191 characters' }
          }}
        />
        <FormInput
          inputName="description"
          labelName="Template Description"
          placeholder="Type the template description here"
          toolTip={inAppText.tooltips.templates.templateBuilder.publishTemplate.templateDescription}
          type="textArea"
          useFormMethods={useFormMethods}
          validationRules={
          { maxLength: { value: 280, message: 'Template Description must have fewer than 280 characters' } }
        }
        />
        {type === 'publish' && (
          <Box mb="26px">
            <InputLabel
              required
              labelName="Folder"
              toolTip={inAppText.tooltips.templates.templateBuilder.publishTemplate.folder}
            />
            <HStack>
              <InfiniteDropdownMenu
                cypressId={CypressIds.reqTemplatesSaveTemplateModalTemplateFolderDropdown}
                data={myFolderData}
                fetchMoreData={fetchNextPage}
                idKey="template-folder-dropdown"
                initialSelect={{
                  id: templateFolder?.id,
                  label: templateFolder?.name,
                  ...templateFolder
                }}
                isDisabled={showCreateFolderInput}
                labelKey="name"
                pageInfo={hasNextPage ? { currentPage: 1, lastPage: 2 } : { currentPage: 1, lastPage: 1 }}
                scrollClassName="template-folder-dropdown"
                variant="primary"
                onSelect={folder => setTemplateFolder(folder)}
              />
              <Box>
                <Tooltip hasArrow borderRadius="md" label={`${showCreateFolderInput ? 'Cancel' : 'Create new folder'}`} placement="bottom">
                  <IconButton
                    aria-label="create-new-template-folder-button"
                    icon={<Icon as={showCreateFolderInput ? FaX : FaPlus} width={10} />}
                    variant="primary"
                    onClick={() => setShowCreateFolderInput(!showCreateFolderInput)}
                  />
                </Tooltip>
              </Box>
            </HStack>
            {showCreateFolderInput && (
              <Box my={4}>
                <FormInput
                  inputName="newFolderName"
                  isDisabled={!showCreateFolderInput}
                  labelName="New Folder"
                  placeholder="Type Your New Folder Name Here"
                  useFormMethods={useFormMethods}
                  validationRules={{
                    required: { value: true, message: 'New Template Folder Name is required' },
                    maxLength: { value: 32, message: 'Template Folder Name must have fewer than 32 characters' }
                  }}
                />
              </Box>
            )}
          </Box>
        )}
        {type === 'draft' && (
          <Box mb="26px">
            <FormInput
              isReadOnly
              inputName="folder"
              labelName="Folder"
              placeholder="Drafts"
              toolTip={inAppText.tooltips.templates.templateBuilder.saveAsDraft.folder}
              useFormMethods={useFormMethods}
            />
          </Box>
        )}

      </form>
      <ModalFooter pt={4}>
        <StackedButtonGroup
          dataAnalyticsId={`save-${type}-requirement-template`}
          label={type === 'draft' ? 'Save Draft' : 'Publish Template'}
          primaryButtonVariant={type === 'draft' ? 'secondary' : 'gradientSuccess'}
          secondaryClick={() => dispatch(hideModal())}
          onClick={handleSubmit(onSubmit)}
        />
      </ModalFooter>
    </Box>
  );
};

SaveTemplateModal.propTypes = {
  type: PropTypes.string.isRequired,
  blueprint: RequirementTemplateBlueprintType
};

export default SaveTemplateModal;
